import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import DraggableDialog from '../components/common/DraggableDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import { Link as RouterLink } from 'react-router-dom';
import apiService from '../services/api.service';
import { ChargeExpenseCodes, PO_CHARGE_TYPES, chargeKey, readChargeExpenseCodes } from '../utils/poCharges';

interface Company {
  companyid: number;
  companycode: string;
  companyname: string;
  databasename: string;
  isactive: boolean;
  docusignenabled: boolean;
  paymentrequestdocusignenabled?: boolean | null;
  paymentrequestprefix?: string | null;
  sapbplid?: string | null;
  autoinvoiceunbilledaccount?: string | null;
  localcurrency?: string | null;
  requirelineproject?: boolean;
  serviceunitpricemustbeone?: boolean;
  invoiceprintenabled?: boolean;
  salesorderprintenabled?: boolean;
  poapprovalsource?: 'PRPO' | 'SAP';
  sapapprovalstartdate?: string | null;
  sapapprovalreviewemail?: string | null;
  sapapprovalemailtemplateid?: number | null;
  sapapprovalautoaddpo?: boolean;
  accountsnotifyenabled?: boolean;
  amountdecimals?: number | null;
  projectreporttemplate?: 'KSA' | 'KWT' | null;
  vatregistered?: boolean;
  vatcountry?: 'SA' | 'AE' | null;
  pettycashenabled?: boolean;
  enquirytoinvoiceenabled?: boolean;
  /** PO charges sent to SAP as freight: charge type → SAP additional expense (utils/poCharges). */
  pochargeexpensecodes?: ChargeExpenseCodes | null;
}

// 3-state Select value for paymentrequestdocusignenabled. NULL is encoded as
// the string 'inherit' in the form so MUI Select stays controlled.
type PRDSChoice = 'inherit' | 'on' | 'off';
const prdsToChoice = (v: boolean | null | undefined): PRDSChoice =>
  v === true ? 'on' : v === false ? 'off' : 'inherit';
const choiceToPRDS = (c: PRDSChoice): boolean | null =>
  c === 'on' ? true : c === 'off' ? false : null;

interface ReportForm {
  logo: string | null;
  name: string;
  address: string;
  foreignName: string;
  foreignAddress: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  taxId: string;
}

const EMPTY_REPORT_FORM: ReportForm = {
  logo: null, name: '', address: '', foreignName: '', foreignAddress: '', phone: '', fax: '', email: '', website: '', taxId: '',
};

/** The Formatted SOA company details (backend services/formattedSoaCompany.service.ts); empty = SAP's value. */
const REPORT_SOA_FIELDS: Array<{ key: Exclude<keyof ReportForm, 'logo' | 'name' | 'address'>; label: string; placeholder: string; multiline?: boolean; rtl?: boolean }> = [
  { key: 'foreignName', label: 'Company name (Arabic)', placeholder: 'e.g. شركة ...', rtl: true },
  { key: 'foreignAddress', label: 'Address (Arabic)', placeholder: '', multiline: true, rtl: true },
  { key: 'phone', label: 'Phone', placeholder: 'e.g. +965 2228 1115' },
  { key: 'fax', label: 'Fax', placeholder: 'e.g. +965 2228 1116' },
  { key: 'email', label: 'E-mail', placeholder: 'e.g. info@example.com' },
  { key: 'website', label: 'Website', placeholder: 'e.g. www.example.com' },
  { key: 'taxId', label: 'Tax / VAT number', placeholder: '' },
];

const CompanyManagement: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<Company | null>(null);
  const [form, setForm] = useState<{
    companycode: string;
    companyname: string;
    databasename: string;
    isactive: boolean;
    docusignenabled: boolean;
    paymentrequestdocusignchoice: PRDSChoice;
    paymentrequestprefix: string;
    sapbplid: string;
    autoinvoiceunbilledaccount: string;
    localcurrency: string;
    requirelineproject: boolean;
    serviceunitpricemustbeone: boolean;
    invoiceprintenabled: boolean;
    salesorderprintenabled: boolean;
    poapprovalsource: 'PRPO' | 'SAP';
    sapapprovalstartdate: string;
    sapapprovalreviewemail: string;
    sapapprovalemailtemplateid: number | '';
    sapapprovalautoaddpo: boolean;
    accountsnotifyenabled: boolean;
    /** '' = by currency (KWD 3, others 2). */
    amountdecimals: number | '';
    /** Projects > Monthly Reports template; '' = none. */
    projectreporttemplate: '' | 'KSA' | 'KWT';
    /** Petty cash: VAT registration, the VAT number format ('' = any 5-20 digits) and the switch. */
    vatregistered: boolean;
    vatcountry: '' | 'SA' | 'AE';
    pettycashenabled: boolean;
    /** Enquiry to Invoice (switched on in one PRPO server only). */
    enquirytoinvoiceenabled: boolean;
    /** Create PO charge type → SAP additional expense; a type left out stays a PRPO estimate. */
    pochargeexpensecodes: ChargeExpenseCodes;
  }>({
    companycode: '',
    companyname: '',
    databasename: '',
    isactive: true,
    docusignenabled: false,
    paymentrequestdocusignchoice: 'inherit',
    paymentrequestprefix: '',
    sapbplid: '',
    autoinvoiceunbilledaccount: '',
    localcurrency: '',
    requirelineproject: false,
    serviceunitpricemustbeone: false,
    invoiceprintenabled: false,
    salesorderprintenabled: false,
    poapprovalsource: 'PRPO',
    sapapprovalstartdate: '',
    sapapprovalreviewemail: '',
    sapapprovalemailtemplateid: '',
    sapapprovalautoaddpo: true,
    accountsnotifyenabled: false,
    amountdecimals: '',
    projectreporttemplate: '',
    vatregistered: false,
    vatcountry: '',
    pettycashenabled: false,
    enquirytoinvoiceenabled: false,
    pochargeexpensecodes: {},
  });
  const [showActiveOnly, setShowActiveOnly] = useState<boolean>(true);
  // The edited company's SAP additional expenses (Freight setup), read live; null while reading.
  const [sapExpenses, setSapExpenses] = useState<Array<{ code: number; name: string }> | null>(null);
  const [sapExpensesError, setSapExpensesError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [templateChoices, setTemplateChoices] = useState<Array<{ id: number; name: string; isDefault: boolean }>>([]);

  // Report logo & header dialog (used on printed warehouse documents).
  const [reportOpen, setReportOpen] = useState<boolean>(false);
  const [reportCompany, setReportCompany] = useState<Company | null>(null);
  const [reportForm, setReportForm] = useState<ReportForm>(EMPTY_REPORT_FORM);
  // SAP's company details (Administration > Company Details), shown beside the Formatted SOA fields.
  const [reportSap, setReportSap] = useState<Record<string, string> | null>(null);
  const [reportSaving, setReportSaving] = useState<boolean>(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const res = await apiService.getCompanies(showActiveOnly);
      setCompanies(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showActiveOnly]);

  const handleOpenCreate = () => {
    setEditing(null);
    setForm({
      companycode: '',
      companyname: '',
      databasename: '',
      isactive: true,
      docusignenabled: false,
      paymentrequestdocusignchoice: 'inherit',
      paymentrequestprefix: '',
      sapbplid: '',
      autoinvoiceunbilledaccount: '',
      localcurrency: '',
      requirelineproject: false,
      serviceunitpricemustbeone: false,
      invoiceprintenabled: false,
      salesorderprintenabled: false,
      poapprovalsource: 'PRPO',
      sapapprovalstartdate: '',
      sapapprovalreviewemail: '',
      sapapprovalemailtemplateid: '',
      sapapprovalautoaddpo: true,
      accountsnotifyenabled: false,
      amountdecimals: '',
      projectreporttemplate: '',
      vatregistered: false,
      vatcountry: '',
      pettycashenabled: false,
      enquirytoinvoiceenabled: false,
      pochargeexpensecodes: {},
    });
    setSaveError(null);
    setOpen(true);
  };

  const handleOpenEdit = (company: Company) => {
    setEditing(company);
    setForm({
      companycode: company.companycode,
      companyname: company.companyname,
      databasename: company.databasename,
      isactive: company.isactive,
      docusignenabled: company.docusignenabled,
      paymentrequestdocusignchoice: prdsToChoice(company.paymentrequestdocusignenabled),
      paymentrequestprefix: company.paymentrequestprefix ?? '',
      sapbplid: company.sapbplid ?? '',
      autoinvoiceunbilledaccount: company.autoinvoiceunbilledaccount ?? '',
      localcurrency: company.localcurrency ?? '',
      requirelineproject: company.requirelineproject === true,
      serviceunitpricemustbeone: company.serviceunitpricemustbeone === true,
      invoiceprintenabled: company.invoiceprintenabled === true,
      salesorderprintenabled: company.salesorderprintenabled === true,
      poapprovalsource: String(company.poapprovalsource || 'PRPO').toUpperCase() === 'SAP' ? 'SAP' : 'PRPO',
      sapapprovalstartdate: company.sapapprovalstartdate ? String(company.sapapprovalstartdate).slice(0, 10) : '',
      sapapprovalreviewemail: company.sapapprovalreviewemail ?? '',
      sapapprovalemailtemplateid: company.sapapprovalemailtemplateid ? Number(company.sapapprovalemailtemplateid) : '',
      sapapprovalautoaddpo: company.sapapprovalautoaddpo !== false,
      accountsnotifyenabled: company.accountsnotifyenabled === true,
      amountdecimals: company.amountdecimals === null || company.amountdecimals === undefined ? '' : Number(company.amountdecimals),
      projectreporttemplate: company.projectreporttemplate === 'KSA' || company.projectreporttemplate === 'KWT' ? company.projectreporttemplate : '',
      vatregistered: company.vatregistered === true,
      vatcountry: company.vatcountry === 'SA' || company.vatcountry === 'AE' ? company.vatcountry : '',
      pettycashenabled: company.pettycashenabled === true,
      enquirytoinvoiceenabled: company.enquirytoinvoiceenabled === true,
      pochargeexpensecodes: readChargeExpenseCodes(company.pochargeexpensecodes),
    });
    setSapExpenses(null);
    setSapExpensesError(null);
    apiService.getCompanySapAdditionalExpenses(company.companyid)
      .then((res: any) => setSapExpenses(Array.isArray(res?.data) ? res.data : []))
      .catch((err: any) => {
        setSapExpenses([]);
        setSapExpensesError(String(err?.response?.data?.message || 'SAP did not list its additional expenses.'));
      });
    if (!templateChoices.length) {
      apiService.getSapApprovalEmailTemplateChoices()
        .then((res: any) => setTemplateChoices(res?.data || []))
        .catch(() => setTemplateChoices([]));
    }
    setSaveError(null);
    setOpen(true);
  };

  const handleSave = async () => {
    setSaveError(null);
    if (editing && form.poapprovalsource === 'SAP' && !form.sapapprovalstartdate) {
      setSaveError('Choose the date from which SAP PO approvals are e-mailed.');
      return;
    }
    try {
      const payload = {
        companycode: form.companycode,
        companyname: form.companyname,
        databasename: form.databasename,
        isactive: form.isactive,
        docusignenabled: form.docusignenabled,
        paymentrequestdocusignenabled: choiceToPRDS(form.paymentrequestdocusignchoice),
        paymentrequestprefix: form.paymentrequestprefix.trim() || null,
        sapbplid: form.sapbplid.trim() || null,
        // Only settable on an existing company: it is validated against that
        // company's synced SAP Chart of Accounts.
        ...(editing ? {
          autoinvoiceunbilledaccount: form.autoinvoiceunbilledaccount.trim() || null,
          localcurrency: form.localcurrency.trim() || null,
          requirelineproject: form.requirelineproject,
          serviceunitpricemustbeone: form.serviceunitpricemustbeone,
          invoiceprintenabled: form.invoiceprintenabled,
          salesorderprintenabled: form.salesorderprintenabled,
          poapprovalsource: form.poapprovalsource,
          sapapprovalstartdate: form.sapapprovalstartdate || null,
          sapapprovalreviewemail: form.sapapprovalreviewemail.trim() || null,
          sapapprovalautoaddpo: form.sapapprovalautoaddpo,
          accountsnotifyenabled: form.accountsnotifyenabled,
          amountdecimals: form.amountdecimals === '' ? null : form.amountdecimals,
          projectreporttemplate: form.projectreporttemplate || null,
          vatregistered: form.vatregistered,
          vatcountry: form.vatcountry || null,
          pettycashenabled: form.pettycashenabled,
          // Enquiry to Invoice (column EnquiryToInvoiceEnabled; the API contract names the field enquiryToInvoiceEnabled).
          enquirytoinvoiceenabled: form.enquirytoinvoiceenabled,
          enquiryToInvoiceEnabled: form.enquirytoinvoiceenabled,
          pochargeexpensecodes: form.pochargeexpensecodes,
          ...(templateChoices.length ? { sapapprovalemailtemplateid: form.sapapprovalemailtemplateid === '' ? null : form.sapapprovalemailtemplateid } : {}),
        } : {}),
      };
      if (editing) {
        await apiService.updateCompany(editing.companyid, payload);
      } else {
        await apiService.createCompany(payload);
      }
      setOpen(false);
      await loadCompanies();
    } catch (e: any) {
      console.error(e);
      const d = e?.response?.data;
      setSaveError(String((Array.isArray(d?.errors) && d.errors.join('; ')) || d?.message || 'Could not save the company'));
    }
  };

  const handleDelete = async (company: Company) => {
    if (!window.confirm(`Deactivate company ${company.companyname}?`)) return;
    try {
      await apiService.deleteCompany(company.companyid);
      await loadCompanies();
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenReport = async (company: Company) => {
    setReportCompany(company);
    setReportError(null);
    setReportForm({ ...EMPTY_REPORT_FORM, name: company.companyname });
    setReportSap(null);
    setReportOpen(true);
    try {
      const res = await apiService.getCompanyReportSettings(company.companyid);
      const d = res?.data ?? {};
      setReportForm({
        logo: d.logo ?? null,
        name: d.name ?? company.companyname,
        address: d.address ?? '',
        foreignName: d.foreignName ?? '',
        foreignAddress: d.foreignAddress ?? '',
        phone: d.phone ?? '',
        fax: d.fax ?? '',
        email: d.email ?? '',
        website: d.website ?? '',
        taxId: d.taxId ?? '',
      });
      setReportSap(d.sap ?? null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogoFile = (file: File | undefined) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setReportError('Please choose an image file (PNG or JPG).');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setReportError('Image is too large (max 2 MB). Use a smaller logo.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setReportError(null);
      setReportForm((f) => ({ ...f, logo: String(reader.result) }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveReport = async () => {
    if (!reportCompany) return;
    setReportSaving(true);
    setReportError(null);
    try {
      await apiService.updateCompanyReportSettings(reportCompany.companyid, {
        logo: reportForm.logo,
        name: reportForm.name,
        address: reportForm.address,
        foreignName: reportForm.foreignName,
        foreignAddress: reportForm.foreignAddress,
        phone: reportForm.phone,
        fax: reportForm.fax,
        email: reportForm.email,
        website: reportForm.website,
        taxId: reportForm.taxId,
      });
      setReportOpen(false);
    } catch (e: any) {
      setReportError(e?.response?.data?.message ?? 'Could not save report settings.');
    } finally {
      setReportSaving(false);
    }
  };

  return (
    <Box component="section" aria-labelledby="company-management-heading" sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 1.5, sm: 0 }}
        mb={2}
      >
        <Typography
          component="h1"
          id="company-management-heading"
          variant="h5"
          sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}
        >
          Company Management
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <FormControlLabel control={<Switch checked={showActiveOnly} onChange={(e) => setShowActiveOnly(e.target.checked)} />} label="Active only" />
            <Button variant="contained" color="primary" onClick={handleOpenCreate}>Add Company</Button>
        </Stack>
      </Stack>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Companies">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Database</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>DocuSign</TableCell>
              <TableCell>PR Prefix</TableCell>
              <TableCell>PR DocuSign</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((c) => (
              <TableRow key={c.companyid} hover>
                <TableCell>{c.companycode}</TableCell>
                <TableCell>{c.companyname}</TableCell>
                <TableCell>{c.databasename}</TableCell>
                <TableCell>{c.isactive ? 'Yes' : 'No'}</TableCell>
                <TableCell>{c.docusignenabled ? 'Yes' : 'No'}</TableCell>
                <TableCell>{c.paymentrequestprefix || '—'}</TableCell>
                <TableCell>
                  {c.paymentrequestdocusignenabled === true
                    ? 'Yes'
                    : c.paymentrequestdocusignenabled === false
                      ? 'No'
                      : `Inherit (${c.docusignenabled ? 'Yes' : 'No'})`}
                </TableCell>
                <TableCell align="right">
                  <IconButton aria-label="report logo and header" title="Report logo & header" onClick={() => handleOpenReport(c)}>
                    <ImageIcon />
                  </IconButton>
                  <IconButton aria-label="edit" onClick={() => handleOpenEdit(c)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton aria-label="delete" onClick={() => handleDelete(c)} disabled={!c.isactive}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && companies.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">No companies found.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DraggableDialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth title={editing ? 'Edit Company' : 'Add Company'} actions={
        <>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSave}>{editing ? 'Save' : 'Create'}</Button>
        </>
      }>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Company Code" value={form.companycode} onChange={(e) => setForm({ ...form, companycode: e.target.value })} required />
            <TextField label="Company Name" value={form.companyname} onChange={(e) => setForm({ ...form, companyname: e.target.value })} required />
            <TextField label="Database Name" value={form.databasename} onChange={(e) => setForm({ ...form, databasename: e.target.value })} required />
            <FormControlLabel control={<Switch checked={form.isactive} onChange={(e) => setForm({ ...form, isactive: e.target.checked })} />} label="Active" />
            <FormControlLabel control={<Switch checked={form.docusignenabled} onChange={(e) => setForm({ ...form, docusignenabled: e.target.checked })} />} label="DocuSign Enabled (POs)" />

            <Box mt={1}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Payment Request settings
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Payment Request Prefix"
                  value={form.paymentrequestprefix}
                  onChange={(e) => setForm({ ...form, paymentrequestprefix: e.target.value.toUpperCase().slice(0, 10) })}
                  helperText="Used in DocRef <Prefix>/<Division>/<Project|CC>/PR/<Seq>/<YY>. Required before any Payment Request can be submitted for this company."
                  inputProps={{ maxLength: 10 }}
                />
                <TextField
                  label="SAP Branch (BPLId)"
                  value={form.sapbplid}
                  onChange={(e) => setForm({ ...form, sapbplid: e.target.value.replace(/\D/g, '').slice(0, 20) })}
                  helperText="SAP Business Place / branch id. Required on sales orders and outgoing payments once multi-branch is enabled in SAP — without it SAP rejects the post with -5002 'Specify an active branch'. Leave blank for single-branch companies."
                  inputProps={{ inputMode: 'numeric', maxLength: 20 }}
                />
                <FormControl fullWidth>
                  <InputLabel id="prds-label">Payment Request DocuSign</InputLabel>
                  <Select
                    labelId="prds-label"
                    label="Payment Request DocuSign"
                    value={form.paymentrequestdocusignchoice}
                    onChange={(e) => setForm({ ...form, paymentrequestdocusignchoice: e.target.value as PRDSChoice })}
                  >
                    <MenuItem value="inherit">
                      Inherit from DocuSign Enabled ({form.docusignenabled ? 'currently On' : 'currently Off'})
                    </MenuItem>
                    <MenuItem value="on">Always On — send Payment Requests through DocuSign</MenuItem>
                    <MenuItem value="off">Always Off — keep Payment Request approvals in-app</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {editing && (
              <Box mt={1}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Auto Invoice settings
                </Typography>
                <TextField
                  fullWidth
                  label="Unbilled Revenue G/L"
                  value={form.autoinvoiceunbilledaccount}
                  onChange={(e) => setForm({ ...form, autoinvoiceunbilledaccount: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 20) })}
                  placeholder="122010400"
                  helperText="SAP control account Auto Invoice posts unbilled revenue to. Leave blank to use 122010400 only where this company's Chart of Accounts names it Unbilled/Accrued Revenue; otherwise Auto Invoice is blocked for this company. Must exist in the synced Chart of Accounts."
                  inputProps={{ maxLength: 20 }}
                />
                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  label="Local Currency"
                  value={form.localcurrency}
                  onChange={(e) => setForm({ ...form, localcurrency: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3) })}
                  placeholder="SAR"
                  helperText="This company's SAP local currency, used to label amounts on Auto Invoice. Leave blank to use the system default."
                  inputProps={{ maxLength: 3 }}
                />
              </Box>
            )}

            {editing && (
              <Box mt={1}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Purchase Requisition rules
                </Typography>
                <FormControlLabel
                  control={<Switch checked={form.requirelineproject} onChange={(e) => setForm({ ...form, requirelineproject: e.target.checked })} />}
                  label="Project required on every line of a cost center PR"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Applies to PRs created or submitted from now on. PRs already in approval keep the rule they were submitted with.
                </Typography>
                <FormControlLabel
                  control={<Switch checked={form.serviceunitpricemustbeone} onChange={(e) => setForm({ ...form, serviceunitpricemustbeone: e.target.checked })} />}
                  label="Service lines must have unit price 1"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  The quantity then carries the amount of each Service line.
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Sales Portal
                </Typography>
                <FormControlLabel
                  control={<Switch checked={form.invoiceprintenabled} onChange={(e) => setForm({ ...form, invoiceprintenabled: e.target.checked })} />}
                  label="A/R invoice printing (dot-matrix, Wooden Bakery invoice form)"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Turns on Sales Portal &gt; Print A/R Invoices for this company. Only for companies that print on that form.
                </Typography>
                <FormControlLabel
                  control={<Switch checked={form.salesorderprintenabled} onChange={(e) => setForm({ ...form, salesorderprintenabled: e.target.checked })} />}
                  label="Sales order printing (dot-matrix form and office laser)"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Turns on Sales Portal &gt; Print Sales Orders for this company. Printers are set up on either print page (Printer setup).
                </Typography>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="amount-decimals-label">Amount decimal places</InputLabel>
                  <Select
                    labelId="amount-decimals-label"
                    label="Amount decimal places"
                    value={form.amountdecimals === '' ? 'auto' : String(form.amountdecimals)}
                    onChange={(e) => setForm({ ...form, amountdecimals: e.target.value === 'auto' ? '' : Number(e.target.value) })}
                  >
                    <MenuItem value="auto">By currency (KWD, BHD, OMR, JOD: 3; others: 2)</MenuItem>
                    {[0, 1, 2, 3, 4].map((n) => <MenuItem key={n} value={String(n)}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block">
                  Decimal places of the amounts on Sales Portal &gt; Customer SOA, on the screen and in the PDF.
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Projects
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="project-report-template-label">Project monthly report template</InputLabel>
                  <Select
                    labelId="project-report-template-label"
                    label="Project monthly report template"
                    value={form.projectreporttemplate}
                    onChange={(e) => setForm({ ...form, projectreporttemplate: e.target.value as '' | 'KSA' | 'KWT' })}
                  >
                    <MenuItem value="">None (no monthly project reports)</MenuItem>
                    <MenuItem value="KSA">Engie KSA (Executive Summary; contract categories)</MenuItem>
                    <MenuItem value="KWT">Dhow Kuwait (MCSR, variation orders, subcontractors)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block">
                  Turns on Project Reports &gt; Monthly Reports for this company, laid out as the chosen Word template.
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Petty cash
                </Typography>
                <FormControlLabel
                  control={<Switch checked={form.pettycashenabled} onChange={(e) => setForm({ ...form, pettycashenabled: e.target.checked })} />}
                  label="Petty cash claims"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Turns on Petty Cash &gt; Claims for this company. Holders, expense categories and the cash supplier are set in Petty Cash Setup.
                </Typography>
                <FormControlLabel
                  control={<Switch checked={form.vatregistered} onChange={(e) => setForm({ ...form, vatregistered: e.target.checked })} />}
                  label="Company is VAT-registered"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  On: each receipt is a VAT invoice (supplier VAT number, amount before VAT, VAT) or a non-VAT receipt booked to the cash supplier. Off: every receipt is non-VAT.
                </Typography>
                <FormControl fullWidth sx={{ mt: 2 }} disabled={!form.vatregistered}>
                  <InputLabel id="vat-country-label">VAT number format</InputLabel>
                  <Select
                    labelId="vat-country-label"
                    label="VAT number format"
                    value={form.vatcountry}
                    onChange={(e) => setForm({ ...form, vatcountry: e.target.value as '' | 'SA' | 'AE' })}
                  >
                    <MenuItem value="">Any (5 to 20 digits)</MenuItem>
                    <MenuItem value="SA">Saudi Arabia (15 digits, starts and ends with 3)</MenuItem>
                    <MenuItem value="AE">UAE TRN (15 digits, starts with 100)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block">
                  Checks the supplier VAT numbers typed on petty cash receipts and in Suppliers.
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Enquiry to Invoice
                </Typography>
                <FormControlLabel
                  control={<Switch checked={form.enquirytoinvoiceenabled} onChange={(e) => setForm({ ...form, enquirytoinvoiceenabled: e.target.checked })} />}
                  label="Enquiry to Invoice"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Turns on enquiries, quotations, jobs and client invoice requests for this company. Switch on in one PRPO server only: the PRPO servers share the SAP databases.
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Purchase Order approval
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="po-approval-source-label">PO approval follows</InputLabel>
                  <Select
                    labelId="po-approval-source-label"
                    label="PO approval follows"
                    value={form.poapprovalsource}
                    onChange={(e) => setForm({ ...form, poapprovalsource: e.target.value as 'PRPO' | 'SAP' })}
                  >
                    <MenuItem value="PRPO">PRPO approval workflow</MenuItem>
                    <MenuItem value="SAP">SAP B1 approval procedures (e-mail approvers)</MenuItem>
                  </Select>
                </FormControl>
                {form.poapprovalsource === 'SAP' && (
                  <Box display="flex" flexDirection="column" gap={2} mt={2}>
                    <TextField
                      type="date"
                      label="E-mail approvals of SAP POs created from"
                      value={form.sapapprovalstartdate}
                      onChange={(e) => setForm({ ...form, sapapprovalstartdate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      required
                      helperText="PO approval requests created in SAP on or after this date are e-mailed to the approvers. Older pending requests are ignored."
                    />
                    <FormControl fullWidth>
                      <InputLabel id="sap-approval-template-label">Approval e-mail template</InputLabel>
                      <Select
                        labelId="sap-approval-template-label"
                        label="Approval e-mail template"
                        value={form.sapapprovalemailtemplateid}
                        onChange={(e) => setForm({ ...form, sapapprovalemailtemplateid: e.target.value === '' ? '' : Number(e.target.value) })}
                      >
                        <MenuItem value="">Default template{templateChoices.find((t) => t.isDefault) ? ` (${templateChoices.find((t) => t.isDefault)?.name})` : ''}</MenuItem>
                        {templateChoices.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Review mode e-mails (optional)"
                      value={form.sapapprovalreviewemail}
                      onChange={(e) => setForm({ ...form, sapapprovalreviewemail: e.target.value })}
                      inputProps={{ inputMode: 'email', maxLength: 255 }}
                      helperText="One or more addresses, separated by commas. While filled, every approval e-mail goes to these addresses only and decisions from e-mail links are disabled. Clear it to start e-mailing the real approvers."
                    />
                    <FormControlLabel
                      control={<Switch checked={form.sapapprovalautoaddpo} onChange={(e) => setForm({ ...form, sapapprovalautoaddpo: e.target.checked })} />}
                      label="Create the purchase order in SAP automatically after the final approval"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1.5 }}>
                      PRPO adds the approved draft as a purchase order in SAP (through its integration user) and e-mails the initiator the PO number.
                      Off: the initiator adds the draft in SAP B1 as before. Never done in review mode.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      SAP keeps the approval workflow; each decision is recorded in SAP under the approver&apos;s own user, with the SAP B1 password an
                      administrator saved (Settings › SAP Approval Workflows › SAP B1 passwords).
                      The DEV and production servers share the same SAP databases: switch a company to SAP on one server only.
                    </Typography>
                  </Box>
                )}
                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={<Switch checked={form.accountsnotifyenabled} onChange={(e) => setForm({ ...form, accountsnotifyenabled: e.target.checked })} />}
                  label="Notify the accounts team when a PO is finally approved"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {form.accountsnotifyenabled
                    ? 'On: every PO finally approved in this company from now on is checked against the rules in Settings › Accounts notifications (payment terms, projects, divisions, departments). Only POs whose payment terms are in an active rule are e-mailed.'
                    : 'Off: no accounts e-mails are sent for this company. The rules stay, ready for when it is switched on. POs approved while it is off are never e-mailed later.'}
                  {' '}
                  <RouterLink to="/settings/accounts-notifications">Set up the rules in Settings › Accounts notifications</RouterLink>
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Purchase Order charges sent to SAP
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Create PO&apos;s Additional Charges. A charge type mapped to one of this company&apos;s SAP additional expenses
                  (SAP: Administration › Setup › General › Freight) goes on the SAP PO as freight; the others stay PRPO estimates
                  in the costing. The PO&apos;s header discount always goes to SAP; its tax never does (SAP adds VAT itself).
                </Typography>
                {sapExpensesError ? <Alert severity="warning" sx={{ mb: 1.5 }}>{sapExpensesError}</Alert> : null}
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {PO_CHARGE_TYPES.map((type) => {
                    const key = chargeKey(type);
                    const current = form.pochargeexpensecodes[key];
                    const options = [...(sapExpenses || [])];
                    if (current && !options.some((o) => o.code === current.code)) options.push({ code: current.code, name: current.name || '' });
                    return (
                      <FormControl key={key} fullWidth size="small">
                        <InputLabel id={`po-charge-${key}`}>{type}</InputLabel>
                        <Select
                          labelId={`po-charge-${key}`}
                          label={type}
                          value={current ? String(current.code) : ''}
                          onChange={(e) => {
                            const code = Number(e.target.value) || 0;
                            const next: ChargeExpenseCodes = { ...form.pochargeexpensecodes };
                            if (code) next[key] = { code, name: options.find((o) => o.code === code)?.name || '' };
                            else delete next[key];
                            setForm({ ...form, pochargeexpensecodes: next });
                          }}
                        >
                          <MenuItem value="">Not sent: PRPO estimate</MenuItem>
                          {options.map((o) => (
                            <MenuItem key={o.code} value={String(o.code)}>{`${o.code} – ${o.name || 'SAP additional expense'}`}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  })}
                </Box>
                {sapExpenses === null && !sapExpensesError ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Reading SAP&apos;s additional expenses…</Typography>
                ) : null}
              </Box>
            )}
            {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          </Box>
      </DraggableDialog>

      <DraggableDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        maxWidth="sm"
        fullWidth
        title={reportCompany ? `Report Logo & Header — ${reportCompany.companyname}` : 'Report Logo & Header'}
        actions={
          <>
            <Button onClick={() => setReportOpen(false)} disabled={reportSaving}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleSaveReport} disabled={reportSaving}>
              {reportSaving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            Used on printed warehouse documents (GRPO / Delivery Order / AR Invoice). The logo appears at the top; the
            legal name &amp; address appear in the document footer.
          </Typography>

          <Box
            sx={{
              border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 100, bgcolor: 'background.default',
            }}
          >
            {reportForm.logo ? (
              <img src={reportForm.logo} alt="Report logo preview" style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }} />
            ) : (
              <Typography variant="body2" color="text.secondary">No logo set</Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" component="label" startIcon={<ImageIcon />}>
              Upload logo
              <input hidden type="file" accept="image/png,image/jpeg" onChange={(e) => handleLogoFile(e.target.files?.[0])} />
            </Button>
            {reportForm.logo && (
              <Button color="error" onClick={() => setReportForm((f) => ({ ...f, logo: null }))}>
                Remove logo
              </Button>
            )}
          </Stack>

          <TextField
            label="Legal company name (footer)"
            value={reportForm.name}
            onChange={(e) => setReportForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Roots Pharmaceutical Company W.L.L"
          />
          <TextField
            label="Address (footer)"
            value={reportForm.address}
            onChange={(e) => setReportForm((f) => ({ ...f, address: e.target.value }))}
            placeholder="e.g. Al-Soor Street, Soor Tower, 23rd Floor, P.P. Box 5110, Safat 13052, Kuwait"
            multiline
            minRows={2}
          />

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Formatted SOA statements</Typography>
          <Typography variant="body2" color="text.secondary">
            The logo, name and address above and these details are printed on Formatted SOA statements. Leave a field
            empty to print SAP&apos;s value (Administration &gt; Company Details){reportSap ? ', shown in grey' : ''}.
          </Typography>
          {REPORT_SOA_FIELDS.map((f) => (
            <TextField
              key={f.key}
              label={f.label}
              value={reportForm[f.key]}
              onChange={(e) => setReportForm((cur) => ({ ...cur, [f.key]: e.target.value }))}
              placeholder={reportSap?.[f.key] ? `SAP: ${reportSap[f.key].replace(/\n/g, ', ')}` : f.placeholder}
              multiline={f.multiline}
              minRows={f.multiline ? 2 : undefined}
              inputProps={f.rtl ? { dir: 'auto' } : undefined}
            />
          ))}

          {reportError && (
            <Typography variant="body2" color="error">{reportError}</Typography>
          )}
        </Box>
      </DraggableDialog>
    </Box>
  );
};

export default CompanyManagement;